// recova GmbH – App-Shell: Auth-Context, Routing, rollenbasierte Guards.
import { createContext, useContext, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { API } from "@/lib/api";
import LandingPage from "@/pages/LandingPage";
import BookingPage from "@/pages/BookingPage";
import TrackingPage from "@/pages/TrackingPage";
import LoginPage from "@/pages/LoginPage";
import OfficeDashboard from "@/pages/OfficeDashboard";
import WarehouseDashboard from "@/pages/WarehouseDashboard";
import TechDashboard from "@/pages/TechDashboard";
import AdminDashboard from "@/pages/AdminDashboard";

export const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export const ROLE_HOME = {
  backoffice: "/app/office",
  technician: "/app/technik",
};

function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);
  useEffect(() => {
    API.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);
  const login = async (email, password) => {
    const r = await API.post("/auth/login", { email, password });
    setUser(r.data);
    return r.data;
  };
  const logout = async () => {
    try {
      await API.post("/auth/logout");
    } catch (e) {}
    setUser(null);
  };
  return <AuthCtx.Provider value={{ user, setUser, login, logout }}>{children}</AuthCtx.Provider>;
}

function Protected({ roles, children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === undefined)
    return (
      <div data-testid="auth-loading" className="flex min-h-screen items-center justify-center text-[#1b4332]">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#1b4332] border-t-transparent" />
      </div>
    );
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={ROLE_HOME[user.role] || "/"} replace />;
  return children;
}

function RoleRedirect() {
  const { user } = useAuth();
  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={ROLE_HOME[user.role] || "/"} replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/buchen" element={<BookingPage />} />
          <Route path="/status/:token" element={<TrackingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/app" element={<RoleRedirect />} />
          <Route path="/app/office" element={<Protected roles={["backoffice"]}><OfficeDashboard /></Protected>} />
          <Route path="/app/lager" element={<Protected roles={["backoffice"]}><WarehouseDashboard /></Protected>} />
          <Route path="/app/technik" element={<Protected roles={["technician", "backoffice"]}><TechDashboard /></Protected>} />
          <Route path="/app/admin" element={<Protected roles={["backoffice"]}><AdminDashboard /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
